<template>
  <div class="timeline-container">
    <div id="timeline" class="timeline" ref="timelineRef">
      <div 
          v-if="hasMoreAbove" 
          class="timeline__arrow timeline__arrow--up" 
          @click="moveUp"
          @keydown.space="moveUp"
          @keydown.enter="moveUp"
          tabindex="0"
      />
      
      <EpochMM
          v-for="epoch in epochsToRender"
          :key="epoch.id"
          :epoch="epoch"
          :index="store.epochs.findIndex(e => e.id === epoch.id)"
          :activeIndex="store.epochs.findIndex(e => e.id === store.activeEpochId)"
          :timelineEl="timelineRef"
      />

      <div 
          v-if="hasMoreBelow" 
          class="timeline__arrow timeline__arrow--down" 
          @click="moveDown"
          @keydown.space="moveDown"
          @keydown.enter="moveDown"
          tabindex="0"
      />
      <div class="timeline__line"></div>      
    </div>
    <div class="timeline__complement-spacing"></div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from "vue";
import { useTimelineMapStore } from "@features/Pages/TimelineMapPage/_js/app/store/timelineMapStore";
import { useVerticalDragScroll } from "@features/Pages/TimelineMapPage/_js/app/composables/mm/useVerticalDragScroll"
import { useWindowListeners } from "@features/Pages/TimelineMapPage/_js/app/composables/mm/useWindowListeners"
import { useTimelinePersistence } from "@features/Pages/TimelineMapPage/_js/app/composables/mm/useTimelinePersistence"
import { useTimelineElementListeners } from "@features/Pages/TimelineMapPage/_js/app/composables/mm/useTimelineElementListeners"
import { useScrollPinning } from "@features/Pages/TimelineMapPage/_js/app/composables/mm/useScrollPinning";
import { readDocumentCssPx } from "@features/Pages/TimelineMapPage/_js/app/services/readDocumentCssPx";
import EpochMM from "../epoch/EpochMM.vue";
import {useTimelineDom} from "@features/Pages/TimelineMapPage/_js/app/composables/mm/useTimelineDom";

const store = useTimelineMapStore();
const activeIndexRef = ref<number>(-1);

const timelineRef = ref<HTMLElement | null>(null);
const { onPointerDown, onPointerMove, onPointerUp, cancelMomentum } = useVerticalDragScroll(timelineRef, 5);
const { goToEpoch } = useTimelineDom(timelineRef);
const { saveActive } = useTimelinePersistence();
const { smoothScrollTo, readCssPx } = useScrollPinning();

const onScroll = (e: Event) => {};
const onResize = (e: Event) => {};

useWindowListeners({
  onScroll,
  onResize,
});

useTimelineElementListeners(timelineRef, {
  onPointerDown,
  onPointerMove,
  onPointerUp,
  blockWheelAndTouch: true
});

const hasMoreAbove = computed(() => activeIndexRef.value > 2);
const hasMoreBelow = computed(() => activeIndexRef.value < store.epochs.length - 4);

const epochsToRender = computed(() => {
  const epochs = [...store.epochs];
  
  const start = Math.max(0, activeIndexRef.value - 2);
  const end   = Math.min(epochs.length, activeIndexRef.value + 4);

  return epochs.slice(start, end);
});

// Change epoch with arrows - to top or bottom epoch
async function moveUp() {
  if (!store.epochs.length) { return; }
  const firstEpoch = store.epochs[0];

  await goToEpoch(firstEpoch.id);
  const titleEl = document.getElementById(`epoch-title-${firstEpoch.id}`) as HTMLElement | null;
  if (!titleEl) { return; }
  await nextTick();
  await new Promise(r => requestAnimationFrame(r));

  const margin = readCssPx(".site--mm", "--epoch-lock-top", 200);
  const disturbanceHeight = readDocumentCssPx("--disturbance-height", 0);
  const rect = titleEl.getBoundingClientRect();
  const targetTop = window.scrollY + rect.top - margin - disturbanceHeight;

  await smoothScrollTo(targetTop, 700);
  titleEl.focus({ preventScroll: true });
}

async function moveDown() {
  if (!store.epochs.length) { return; }
  const lastEpoch = store.epochs[store.epochs.length - 1];

  await goToEpoch(lastEpoch.id);
  const titleEl = document.getElementById(`epoch-title-${lastEpoch.id}`) as HTMLElement | null;
  if (!titleEl) { return; }
  await nextTick();
  await new Promise(r => requestAnimationFrame(r));

  const margin = readCssPx(".site--mm", "--epoch-lock-top", 200);
  const disturbanceHeight = readDocumentCssPx("--disturbance-height", 0);
  const rect = titleEl.getBoundingClientRect();
  const targetTop = window.scrollY + rect.top - margin - disturbanceHeight;

  await smoothScrollTo(targetTop, 700);
  titleEl.focus({ preventScroll: true });
}

onMounted(async () => {  
  if ('scrollRestoration' in history) { history.scrollRestoration = 'manual'; }
  
  if (store.epochs.length > 0) {
    store.initEpochs(store.epochs, undefined);
    store.ensureActiveEpochVisibility();
    await nextTick();
    await new Promise(r => requestAnimationFrame(r));
    window.scrollTo({ top: 0, behavior: 'auto' });
  }
});

onUnmounted(() => {
  cancelMomentum();
});

watch(
    () => store.activeEpochId,
    (id) => {
      activeIndexRef.value = store.epochs.findIndex((e) => e.id === id);
      store.ensureActiveEpochVisibility();
      if (id != null) { saveActive(id); } 
    },
    { immediate: true }
);
</script>
